import { DialogComponent } from '@theme/dialog';

/**
 * The header's wishlist drawer (snippets/forcast-wishlist-drawer.liquid): one
 * row per saved product, newest first.
 *
 * The list lives in the shopper's browser (assets/forcast-wishlist.js), so
 * Liquid cannot render it. Each row is fetched as
 * sections/forcast-wishlist-item.liquid in its own product's context instead,
 * which keeps every price, size and availability rule the live Liquid one, in
 * the shopper's market. Nothing is fetched until the drawer opens, and a reopen
 * with nothing changed redraws nothing. Rows are laid down in list order before
 * any request goes out, so rows arriving out of order never reshuffle the list.
 *
 * A dialog-component (assets/dialog.js) underneath, so focus, Escape, the
 * click outside and the scroll lock behave as in every other theme drawer.
 */

const ITEM_SECTION = 'forcast-wishlist-item';
/** Rows fetched at once: enough to fill the drawer quickly without queueing the whole list on the connection pool. */
const CONCURRENCY = 6;
const CHANGE_EVENT = 'forcast:wishlist-change';

/**
 * @typedef {{ id: string, handle: string, at: number }} WishlistItem
 */

/**
 * Runs `task` over `list` with at most `limit` running at once.
 *
 * @template T
 * @param {T[]} list
 * @param {number} limit
 * @param {(item: T) => Promise<void>} task
 */
async function eachLimited(list, limit, task) {
  let next = 0;
  const worker = async () => {
    while (next < list.length) {
      const item = list[next++];
      if (item !== undefined) await task(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
}

class ForcastWishlistDrawer extends DialogComponent {
  /** Bumped by every render, so a response that lands after a newer render started is dropped. */
  #generation = 0;
  /** The ids the list was last drawn from (null until the first open), so an unchanged reopen redraws nothing. */
  #drawn = /** @type {string | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.addEventListener('forcast:quick-added', this.#onQuickAdded);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.removeEventListener('forcast:quick-added', this.#onQuickAdded);
  }

  showDialog() {
    super.showDialog();
    this.#render();
  }

  get #isOpen() {
    return Boolean(this.refs.dialog?.open);
  }

  // A size tapped in a row went to the bag (assets/quick-add-sizes.js), which
  // opens the bag drawer as its confirmation; step aside so it is the one seen.
  #onQuickAdded = () => {
    this.closeDialog();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    // Closed, the list is simply drawn afresh on the next open.
    if (!this.#isOpen) return;

    const { action, item } = /** @type {CustomEvent} */ (event).detail ?? {};

    // A bookmark in a row was un-tapped: fold the row away where it stands rather
    // than redrawing the list under the shopper's pointer.
    if (action === 'remove' && item) {
      const slot = this.#slot(item.id);
      if (slot) this.#leave(slot);
      this.#drawn = this.#ids(window.forcastWishlist?.items() ?? []);
      this.#updateCount();
      return;
    }

    this.#render();
  };

  /**
   * Folds a row away (the drawer's stylesheet closes its grid track), then
   * removes it. The count stops including it at once; the timeout covers a
   * reduced-motion visitor, for whom nothing transitions.
   *
   * @param {Element} slot
   */
  #leave(slot) {
    if (slot.classList.contains('is-leaving')) return;
    slot.classList.add('is-leaving');
    let done = false;
    const remove = () => {
      if (done) return;
      done = true;
      slot.remove();
      this.#updateCount();
    };
    slot.addEventListener('transitionend', (event) => {
      if (event.target === slot && event.propertyName === 'grid-template-rows') remove();
    });
    setTimeout(remove, 450);
  }

  /** @param {WishlistItem[]} items */
  #ids(items) {
    return items.map((item) => item.id).join(',');
  }

  /** @param {string} id */
  #slot(id) {
    return this.querySelector(`[data-wishlist-slot="${CSS.escape(id)}"]:not(.is-leaving)`);
  }

  async #render() {
    const items = window.forcastWishlist?.items() ?? [];
    const ids = this.#ids(items);
    if (ids === this.#drawn) {
      this.#updateCount();
      return;
    }
    this.#drawn = ids;

    const generation = ++this.#generation;
    const list = this.querySelector('[data-wishlist-grid]');
    const skeleton = this.querySelector('template[data-wishlist-skeleton]');
    if (!list || !(skeleton instanceof HTMLTemplateElement)) return;

    list.replaceChildren(
      ...items.map((item) => {
        const slot = document.createElement('li');
        slot.className = 'forcast-wishlist-drawer__slot';
        slot.dataset.wishlistSlot = item.id;
        slot.append(skeleton.content.cloneNode(true));
        return slot;
      })
    );
    this.#updateCount();

    await eachLimited(items, CONCURRENCY, async (item) => {
      const row = await this.#fetchRow(item);
      if (generation !== this.#generation) return;

      const slot = this.#slot(item.id);
      if (!slot) return; // Removed while it loaded.

      if (row) {
        slot.replaceChildren(row);
        return;
      }

      // The product is gone. Dropping it is housekeeping, not the shopper
      // removing something, so it is kept out of the analytics events.
      window.forcastWishlist?.remove({ id: item.id }, { publish: false });
    });
  }

  /**
   * @param {WishlistItem} item
   * @returns {Promise<Element | null>} The row; null when the product no longer
   *   exists (the section's missing marker, a 404, or the handle now belonging to
   *   another product); a fallback row for any other failure, which may pass.
   */
  async #fetchRow(item) {
    if (!item.handle) return this.#fallbackRow(item);

    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}?section_id=${ITEM_SECTION}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      // A handle that no longer resolves still answers 200: the section renders
      // with a blank product and says so (sections/forcast-wishlist-item.liquid).
      if (html.querySelector('[data-wishlist-missing]')) return null;
      const row = html.querySelector('.forcast-wishlist-item');
      // No row and no marker is a broken render, not a deleted product: keep the item.
      if (!row) throw new Error('No row in the response');

      // The handle now belongs to another product: the saved one was deleted
      // and its handle reused.
      if (row.getAttribute('data-product-id') !== item.id) return null;

      // A renamed product answers through its redirect. Keep the new handle so
      // the next open asks for it directly.
      const handle = row.getAttribute('data-product-handle');
      if (handle && handle !== item.handle) window.forcastWishlist?.add({ id: item.id, handle });

      return row;
    } catch (error) {
      console.warn(`Wishlist: could not load ${item.handle}`, error);
      return this.#fallbackRow(item);
    }
  }

  /**
   * Shown when a row could not be fetched for a reason that may pass (a network
   * blip, a 5xx). The item stays saved; the shopper can still reach it.
   *
   * @param {WishlistItem} item
   */
  #fallbackRow(item) {
    const row = document.createElement('div');
    row.className = 'forcast-wishlist-item forcast-wishlist-item--error';

    const message = document.createElement('p');
    message.className = 'forcast-wishlist-item__status';
    message.textContent = this.dataset.error ?? '';
    row.append(message);

    if (item.handle) {
      const link = document.createElement('a');
      link.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(item.handle)}`;
      link.textContent = this.dataset.viewProduct ?? '';
      row.append(link);
    }

    return row;
  }

  #updateCount() {
    const count = this.querySelectorAll('[data-wishlist-slot]:not(.is-leaving)').length;
    const label = (count === 1 ? this.dataset.labelOne : this.dataset.labelOther) ?? '';

    const output = this.querySelector('[data-wishlist-count]');
    if (output) output.textContent = count ? label.replace('[count]', String(count)) : '';

    // The empty state waits for a last row to finish folding away.
    const empty = this.querySelector('[data-wishlist-empty]');
    if (empty instanceof HTMLElement) empty.hidden = this.querySelector('[data-wishlist-slot]') !== null;
  }
}

if (!customElements.get('forcast-wishlist-drawer')) {
  customElements.define('forcast-wishlist-drawer', ForcastWishlistDrawer);
}
